<template>
  <div class="base-container">
    <FaqPanel
      page="campaigns"
      :top-offset="50"
      :max-width="350"
      :backdrop="true"
    />
    <MalwareModal
      :id="current.id"
      v-model="malwareWindowIsVisible"
      :name="current.name"
      type="Creature"
    />

    <div v-if="campaignGroupsEnabled" class="tabs-container">
      <el-tabs v-model="activeTab" class="campaigns-tabs">
        <el-tab-pane label="Campaigns List" name="campaigns" />
        <el-tab-pane label="Campaign Groups" name="groups" />
      </el-tabs>
    </div>

    <template v-if="activeTab === 'campaigns'">
      <el-container v-if="!loading && notFiltered && items.length < 1">
        <el-main class="shadow pb-none">
          <EmptyCampaignsBlock />
        </el-main>
      </el-container>
      <el-container v-else class="campaigns-page">
        <el-main class="shadow">
          <Sticky :z-index="10" :sticky-top="50" class-name="filter-panel">
            <FilterPanel
              v-model="filter"
              :reset-value="defaultFilter"
              @update="update"
            />
          </Sticky>
        </el-main>
      </el-container>
      <el-main class="new-campaign-block">
        <router-link to="/campaigns/new">
          <el-button
            class="mr-1"
            type="primary"
            size="small"
            icon="PlusElementIcon"
            round
          >
            Add campaign
          </el-button>
        </router-link>
      </el-main>
      <LoadingBlock :loading="loading" />
      <div v-if="!loading && items.length > 0">
        <CampaignCard
          v-for="item in items"
          :key="item.id"
          :campaign="item"
          :pulsating="pulsating"
        />
        <el-main>
          <Pagination
            v-if="total > 0"
            :total="total"
            :page="page"
            @pagination="update"
          />
        </el-main>
      </div>
      <div v-if="!loading && items.length < 1" class="empty-result-wrapper">
        <div class="el-alert el-alert--warning is-light empty-result">
          <h4 class="empty-result-text">
            We haven't found any matches for your request, you can
            <el-button
              class="reset-filters-button el-tag el-tag--danger"
              icon="RefreshElementIcon"
              size="small"
              @click="reset"
            >
              reset filters
            </el-button>
          </h4>
        </div>
      </div>
    </template>

    <template v-else-if="activeTab === 'groups' && campaignGroupsEnabled">
      <CampaignGroupsList />
    </template>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  ref,
  onMounted,
  onUnmounted,
  provide
} from 'vue'
import { useCampaignsStore } from '@/apps/traforama/store/campaigns'
import { useCreativesStore } from '@/apps/traforama/store/creatives'
import { useUserStore } from '@/store/modules/user'
import CampaignCard from './components/CampaignCard.vue'
import EmptyCampaignsBlock from './components/EmptyCampaignsBlock.vue'
import FilterPanel from './components/FilterPanel.vue'
import LoadingBlock from '@/components/LoadingBlock.vue'
import Pagination from '@/components/Pagination.vue'
import Sticky from '@/components/Sticky.vue'
import FaqPanel from '@/components/FaqPanel/Index.vue'
import MalwareModal from '@/apps/traforama/components/malware/Modal.vue'
import CampaignGroupsList from './components/CampaignGroupsList.vue'

export default defineComponent({
  name: 'CampaignsList',
  components: {
    CampaignCard,
    LoadingBlock,
    EmptyCampaignsBlock,
    Pagination,
    FilterPanel,
    Sticky,
    FaqPanel,
    MalwareModal,
    CampaignGroupsList
  },
  setup() {
    const creativesStore = useCreativesStore()
    const campaignsStore = useCampaignsStore()
    const userStore = useUserStore()

    const campaignGroupsEnabled = computed(() =>
      userStore.featureEnabled('campaign_groups')
    )

    const total = computed(() => campaignsStore.total)
    const items = computed(() => campaignsStore.currentItems)
    const page = computed(() => campaignsStore.currentPage)
    const loading = computed(
      () =>
        Object.keys(campaignsStore.itemsLoading).length > 0 ||
        campaignsStore.listLoading
    )

    const filter = computed({
      get: () => campaignsStore.filter,
      set: (val: any) => campaignsStore.SetFilters(val)
    })
    const defaultFilter = computed(() => campaignsStore.defaultFilter)
    const activeTab = ref('campaigns')
    const syncIntervalValue = 7_500
    let syncTimer: ReturnType<typeof setInterval> | undefined
    let syncInProgress = false

    const pendingSyncIds = () => {
      const ids = campaignsStore.currentItems.flatMap((campaign) => {
        return (
          campaign.relationships?.creatures?.data.map((entry) => entry.id) || []
        )
      })

      return [...new Set(ids)].filter((id) => {
        const creative = creativesStore.items[id]?.attributes
        return creative?.show_processing && !creative.asset_processed
      })
    }

    const stopSyncStatusesPolling = () => {
      if (!syncTimer) return

      clearInterval(syncTimer)
      syncTimer = undefined
    }

    const syncCreativeStatuses = async () => {
      const ids = pendingSyncIds()
      if (ids.length === 0) {
        stopSyncStatusesPolling()
        return
      }
      if (syncInProgress) return

      syncInProgress = true
      try {
        await creativesStore.FetchSyncStatuses(ids)
      } catch {
        stopSyncStatusesPolling()
      } finally {
        syncInProgress = false
      }
    }

    const startSyncStatusesPolling = () => {
      stopSyncStatusesPolling()
      if (pendingSyncIds().length === 0) return

      syncTimer = setInterval(syncCreativeStatuses, syncIntervalValue)
    }

    const update = async ({
      limit = 15,
      newPage = 1
    }: {
      limit?: number
      newPage?: number
    }) => {
      campaignsStore.SetCurrentPage(newPage)
      await campaignsStore.FetchItems()
      startSyncStatusesPolling()
    }

    const reset = () => {
      campaignsStore.SetFilters(JSON.parse(JSON.stringify(defaultFilter.value)))
      update({})
    }

    const disabled = computed(() => {
      return Object.values(creativesStore.selected).length < 1
    })

    const notFiltered = computed(() => {
      return Object.values(filter.value).length < 1
    })

    const pulsating = /puls/.test(window.location.hash)
    const current = ref<any>({})
    const malwareWindowIsVisible = ref(false)

    const openMalwareModal = (item: any) => {
      current.value = item
      malwareWindowIsVisible.value = true
    }

    provide('openMalwareModal', openMalwareModal)

    onMounted(() => {
      update({})
    })

    onUnmounted(stopSyncStatusesPolling)

    return {
      items,
      loading,
      total,
      page,
      update,
      filter,
      defaultFilter,
      disabled,
      notFiltered,
      reset,
      pulsating,
      malwareWindowIsVisible,
      current,
      activeTab,
      campaignGroupsEnabled
    }
  }
})
</script>

<style lang="scss" scoped>
.campaigns-page {
  display: flex;
  justify-content: space-between;
}

.new-campaign-block {
  display: flex;
  justify-content: flex-end;
  padding: 8px 32px 8px 20px;
}

.empty-result-wrapper {
  box-sizing: border-box;
  padding: 0 20px;
  width: 100%;
}

.empty-result {
  margin: 0;
  padding: 30px 20px;
  width: 100%;
}

.empty-result-text {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: 0;
}

.reset-filters-button {
  cursor: pointer;
  flex-shrink: 0;
}

.el-container {
  margin: 0;
}

:deep(.filter-panel) {
  border-radius: 5px;
}

:deep(.el-card__body) {
  padding: 0px 5px;
}

:deep(h3) {
  margin: 2px 8px 5px 8px;
  padding-top: 5px;
}

:deep(.el-loading-mask) {
  margin: 0px 20px;
}

:deep(.el-card.is-always-shadow) {
  box-shadow: none;
}

.tabs-container {
  padding: 0 20px;
  margin-bottom: 10px;
}

.campaigns-tabs {
  :deep(.el-tabs__header) {
    margin-bottom: 0;
  }

  :deep(.el-tabs__item) {
    font-size: 14px;
    font-weight: 500;
  }

  :deep(.el-tabs__item.is-active) {
    color: #6366f1;
  }

  :deep(.el-tabs__active-bar) {
    background-color: #6366f1;
  }
}
</style>
